import React, { useState } from 'react';
import { Box, Avatar, Typography, Link as MuiLink } from '@mui/material';
import { Campaign as CampaignIcon, Circle as CircleIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useUser } from 'shared/helpers/axios'; // Adjust import based on your user hook
import { useTheme } from '@mui/material/styles';

// Helper to compute initials from a name
const initialsFromName = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + second).toUpperCase();
};

export function SuggestionNotificationCard({
  notification,
  onMarkRead,
}) {
  const navigate = useNavigate();
  const { user: currentUser } = useUser(); // Get current user
  const payload = notification.payload || {};

  // console.log('#### SuggestionNotification', payload)
  const theme = useTheme();
  const authorName = payload.author_name || 'Someone';
  const authorId = payload.author_id;
  const authorAvatarUrl = payload.author_avatar_url || null;
  const authorInitials = initialsFromName(authorName);
  const targetType = (payload.target_type || '').toLowerCase();
  const targetName = payload.target_name || 'item';
  const targetId = payload.target_id;
  const recipientName = payload.recipient_name || currentUser?.name || 'you';
  


   // Check if the author is the current user
  const isCurrentUser = currentUser && authorId && String(currentUser.id) === String(authorId);
  
  // Use "You" if author is current user, otherwise use author name
  const displayAuthorName = isCurrentUser ? 'You' : authorName;
  
  
  const authorProfileUrl = authorId ? `/profile/${authorId}` : null;

  // Determine if we should show a clickable author link
  const shouldShowAuthorLink = authorProfileUrl && !isCurrentUser;

  const [imgBroken, setImgBroken] = useState(false);
  const showInitials = !authorAvatarUrl || imgBroken;

  // Get URL based on target type
  const getTargetUrl = () => {
    if (!targetId) return null;
    switch (targetType) {
      case 'call':
        return `/funding/call/${targetId}`;
      case 'consortium':
        return `/consortium/${targetId}`;
      case 'user':
        return `/profile/${targetId}`;
      default:
        return null;
    }
  };

  const targetUrl = getTargetUrl();

  const handleMarkRead = () => {
    if (!notification.is_read && onMarkRead) {
      onMarkRead();
    }
  };

  const handleAuthorClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleMarkRead();
    if (authorProfileUrl && !isCurrentUser) {
      navigate(authorProfileUrl);
    }
  };

  const handleCardClick = () => {
    handleMarkRead();
    if (targetUrl) {
      navigate(targetUrl);
    }
  };

  // Get type display text
  const getTypeText = () => {
    switch (targetType) {
      case 'call': return 'call';
      case 'consortium': return 'consortium';
      case 'user': return 'connection';
      case 'funding_agency': return 'funding agency';
      case 'funding_program': return 'funding program';
      default: return 'item';
    }
  };

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
        transition: 'background-color 0.2s',
        cursor: targetUrl ? 'pointer' : 'default',
        '&:hover': {
          backgroundColor: 'action.selected',
        },
      }}
    >
      {/* Avatar - Show current user's avatar or author's avatar */}
      <Avatar
        src={showInitials ? undefined : authorAvatarUrl}
        alt={authorName || 'User'}
        imgProps={{ onError: () => setImgBroken(true) }}
        sx={{
          width: 32,
          height: 32,
          
          backgroundColor: theme.palette.primary.avatar,
          color: 'white',

          cursor: shouldShowAuthorLink ? 'pointer' : 'default',
          
        }}
        onClick={shouldShowAuthorLink ? handleAuthorClick : undefined}
      >
        {showInitials ? 
          (isCurrentUser ? 
            (currentUser?.first_name ? initialsFromName(currentUser.first_name) : 'Y') 
            : authorInitials
          ) : 
          <CampaignIcon fontSize="small" />
        }
      </Avatar>

      {/* Notification text */}
      <Typography variant="body2" sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {shouldShowAuthorLink ? (
          <MuiLink
            component="a"
            href={authorProfileUrl}
            underline="hover"
            color="text.primary"
            fontWeight={600}
            onClick={handleAuthorClick}
            sx={{ cursor: 'pointer' }}
          >
            {displayAuthorName}
          </MuiLink>
        ) : (
          <Box component="span" fontWeight={600} color="text.primary">
            {displayAuthorName}
          </Box>
        )}
        
        <Box component="span" color="text.secondary">
          {isCurrentUser ? ' suggested a ' : ' suggested a '}
        </Box>
        
        <Box component="span" color="text.secondary">
          {getTypeText()} to
        </Box>
        
        <Box component="span" fontWeight={500} color={"text.primary"}>
           {recipientName}
        </Box>
        


        {targetName && (
          <>
            <Box component="span" color="text.secondary">:</Box>
            <Box component="span" color="text.primary" fontWeight={500}>
              {targetName}
            </Box>
          </>
        )}
      </Typography>

      {/* Timestamp and unread indicator */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
        </Typography>
        {!notification.is_read && (
          <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
        )}
      </Box>
    </Box>
  );
}

export default SuggestionNotificationCard;